import {determineLife, splitPeriodsForRetirement} from 'services/personFunctions';
import {defaultIncomePeriodTemplate, personTemplate} from 'store/modules/shared/templates';
import {validDate} from 'components/form/validator/validations';
import Api from "services/Api";
import _ from 'lodash';
import moment from 'moment';
import Validator from 'services/Validator';
import Vue from 'vue';

/**
 * Mutation Types
 *
 * @type {Object}
 */
const mutationTypes = {
    SET: 'SET',
    UPDATE_CLAIMANT_VALUE: 'UPDATE_CLAIMANT_VALUE',
    UPDATE_CLAIMANT_DETAIL: 'UPDATE_CLAIMANT_DETAIL',
    UPDATE_LIFE_EXPECTANCY: 'UPDATE_LIFE_EXPECTANCY',
    UPDATE_LIFE_ASSUMED_EXPECTANCY: 'UPDATE_LIFE_ASSUMED_EXPECTANCY',
    UPDATE_INCOME: 'UPDATE_INCOME',
    ADD_INCOME_PERIOD: 'ADD_INCOME_PERIOD',
    UPDATE_INCOME_PERIOD: 'UPDATE_INCOME_PERIOD',
    DELETE_INCOME_PERIOD: 'DELETE_INCOME_PERIOD',
    UPDATE_STATE_PENSION_AGE: 'UPDATE_STATE_PENSION_AGE',
    UPDATE_CLAIM_INFO_VALUE: 'UPDATE_CLAIM_INFO_VALUE',
    RESET_EMPLOYED_INCOME: 'RESET_EMPLOYED_INCOME',
    RESET_PENSION_INCOME: 'RESET_PENSION_INCOME',
    RESET: 'RESET',
    UPDATING_INCOME_PERIOD: 'UPDATING_INCOME_PERIOD'
};

/**
 * Initial State
 *
 * @type {Object}
 */
const state = {
    // Person
    theClaimant: personTemplate(),
    // Claimant Details
    claimantDetails: {
        claimNarrative: null,
        diseaseOrAccident: null,
        diseaseType: null,
        diseaseTypeOther: '',
        accidentType: null,
        accidentTypeOther: '',
        defendantName:  {
            id: null,
            name: null,
            type: null
        },
        claimantSolicitor: {
            id: null,
            name: null,
            type: null
        },
        claimantReference: null,
        yourReference: null,
    },
    claimInfo: {
        discountRate: -0.25,
        periodBetweenOnsetAndAssumedDod: 0,
        claimantReductionInLife: 0,
        proceduralStatus: 'pre-lit',
        dateOfIssueProceedings: null,
        dateOfServiceProceedings: null,
        dateOfAssumedSettlement: null,
    },
    updatingIncome: false,
};

/**
 * Validation Rules.
 *
 * @type {Object}
 */
let rules = {
    'theClaimant.name': {type: 'text', rules: 'required'},
    'theClaimant.gender': {type: 'text', rules: 'required'},
    'theClaimant.dateOfBirth': {type: 'date', rules: 'required'},
    'claimantDetails.claimantSolicitor': {type: 'text', rules: 'required'},
    'claimantDetails.claimantReference': {type: 'text', rules: 'required'},
    'claimantDetails.diseaseOrAccident': {type: 'text', rules: 'required'},
    'claimantDetails.claimNarrative': {type: 'text', rules: 'required'},
    'claimantDetails.defendantName': {type: 'text', rules: 'required'},
};

const numberVal = value => parseFloat(value || 0);

/**
 * Cycle over an object and dispatch an update for each key.
 *
 * @param {function} dispatch
 * @param {string} action
 * @param {{}} obj
 * @param {number|null} [index]
 *
 * @return {Promise}
 */
const updateObject = (dispatch, action, obj, index = null) => {
    if (typeof obj !== 'object') {
        return Promise.all([]);
    }

    const proms = Object.keys(obj).map(key => dispatch(action, {key, value: obj[key], index}));
    return Promise.all(proms);
};

/**
 * Throttle update of person life expectancy to allow for typing
 */
export const throttleUpdateLife = _.debounce((dispatch, getters, action, person, lifeExpectancy, index) => {
    if (validDate(person.dateOfBirth)) {
        if (lifeExpectancy.method == 'assumed_percentage_of_life') {
            lifeExpectancy.method = 'percentage_of_normal_life_expectancy';
        }

        if (lifeExpectancy.method == 'assumed_reduction_in_life') {
            lifeExpectancy.method = 'percentage_reduction_in_normal_life_expectancy';
        }

        let assumedSettlement = getters['lost/claimant/getClaimantInfoValue']('dateOfAssumedSettlement');

        determineLife(assumedSettlement, person, lifeExpectancy)
            .then(updatedLife => {
                return updateObject(dispatch, action, updatedLife, index);
            })
            .then(() => {
                return dispatch('updateReductionInLife');
            })
            .then(() => {
                if (state.theClaimant.income.futurePeriods.length) {
                    dispatch('updateIncomePeriodValue', {
                        index: (state.theClaimant.income.futurePeriods.length - 1),
                        key: 'endOfPeriod',
                        value: state.theClaimant.lifeExpectancy.dateOfDeath,
                        type: 'future'
                    })
                }
            });
    }
}, 500);

/**
 * Calculate the missing parts of assumed life expectancy
 */
const calcAssumedLifeExpectancy = ({state, assumedLifeExpectancy}) => {
    if (
        assumedLifeExpectancy.ageAtDeath
        || assumedLifeExpectancy.dateOfDeath
        || assumedLifeExpectancy.yearsLeft
        || assumedLifeExpectancy.monthsLeft
        || assumedLifeExpectancy.percentageOfNormalLifeExpectancy !== null
        || assumedLifeExpectancy.percentageReductionInNormalLifeExpectancy !== null
    ) {
        switch (assumedLifeExpectancy.method) {
            case 'assumed_years_and_months_left':
                return {
                    method: 'assumed_years_and_months_left',
                    yearsLeft: assumedLifeExpectancy.yearsLeft,
                    monthsLeft: assumedLifeExpectancy.monthsLeft,
                    dateOfDeath: moment().add(assumedLifeExpectancy.yearsLeft, 'years')
                        .add(assumedLifeExpectancy.monthsLeft, 'months')
                        .format('DD/MM/YYYY'),
                    ageAtDeath: moment().add(assumedLifeExpectancy.yearsLeft, 'years')
                        .add(assumedLifeExpectancy.monthsLeft, 'months')
                        .diff(moment(state.theClaimant.dateOfBirth, 'DD/MM/YYYY'), 'days') / 365.25,
                    percentageOfNormalLifeExpectancy: 100,
                    percentageReductionInNormalLifeExpectancy: 0,
                };
            case 'assumed_date':
                return {
                    method: 'assumed_date',
                    yearsLeft: assumedLifeExpectancy.yearsLeft,
                    monthsLeft: assumedLifeExpectancy.monthsLeft,
                    dateOfDeath: assumedLifeExpectancy.dateOfDeath,
                    ageAtDeath: moment(assumedLifeExpectancy.dateOfDeath, 'DD/MM/YYYY')
                        .diff(moment(state.theClaimant.dateOfBirth, 'DD/MM/YYYY'), 'days') / 365.25,
                    percentageOfNormalLifeExpectancy: 100,
                    percentageReductionInNormalLifeExpectancy: 0,
                };
            case 'assumed_age_at_death':
                let assumedDateDiff = moment(state.theClaimant.dateOfBirth, 'DD/MM/YYYY')
                    .add(assumedLifeExpectancy.ageAtDeath, 'years')
                    .diff(moment(), 'months');

                return {
                    method: 'assumed_age_at_death',
                    yearsLeft: Math.floor(assumedDateDiff / 12),
                    monthsLeft: assumedDateDiff - (Math.floor(assumedDateDiff / 12) * 12),
                    dateOfDeath: moment(state.theClaimant.dateOfBirth, 'DD/MM/YYYY')
                        .add(assumedLifeExpectancy.ageAtDeath, 'years').format('DD/MM/YYYY'),
                    ageAtDeath: assumedLifeExpectancy.ageAtDeath,
                    percentageOfNormalLifeExpectancy: 100,
                    percentageReductionInNormalLifeExpectancy: 0,
                };
            case 'assumed_percentage_of_life':
                assumedLifeExpectancy.percentageOfNormalLifeExpectancy = assumedLifeExpectancy.percentageOfNormalLifeExpectancy || 0;

                return {
                    method: 'assumed_percentage_of_life',
                    yearsLeft: assumedLifeExpectancy.yearsLeft,
                    monthsLeft: assumedLifeExpectancy.monthsLeft,
                    dateOfDeath: assumedLifeExpectancy.dateOfDeath,
                    ageAtDeath: assumedLifeExpectancy.ageAtDeath,
                    percentageOfNormalLifeExpectancy: assumedLifeExpectancy.percentageOfNormalLifeExpectancy,
                    percentageReductionInNormalLifeExpectancy: 100 - assumedLifeExpectancy.percentageOfNormalLifeExpectancy,
                };
            case 'assumed_reduction_in_life':
                assumedLifeExpectancy.percentageReductionInNormalLifeExpectancy = assumedLifeExpectancy.percentageReductionInNormalLifeExpectancy || 0;

                return {
                    method: 'assumed_reduction_in_life',
                    yearsLeft: assumedLifeExpectancy.yearsLeft,
                    monthsLeft: assumedLifeExpectancy.monthsLeft,
                    dateOfDeath: assumedLifeExpectancy.dateOfDeath,
                    ageAtDeath: assumedLifeExpectancy.ageAtDeath,
                    percentageOfNormalLifeExpectancy: 100 - assumedLifeExpectancy.percentageReductionInNormalLifeExpectancy,
                    percentageReductionInNormalLifeExpectancy: assumedLifeExpectancy.percentageReductionInNormalLifeExpectancy,
                };
        }
    }

    return assumedLifeExpectancy;
};

/**
 * Find the next start date for anincome period.
 */
const findNextPeriodStart = (periods, worseCase) => periods.reduce((min, period) => {
    const periodDate = moment(period.endOfPeriod, 'DD/MM/YYYY');
    return periodDate.isAfter(min) ? periodDate : min;
}, moment(worseCase, 'DD/MM/YYYY')).format('DD/MM/YYYY');

/**
 * Find the next end date for an income period.
 */
const findNextPeriodEnd = (periods, worseCase) => {
    return periods.reduce((max, period) => {
        let periodDate = moment(period.startOfPeriod, 'DD/MM/YYYY');
        return periodDate.isAfter(max) ? periodDate : max;
    }, moment(worseCase, 'DD/MM/YYYY')).format('DD/MM/YYYY');
};

/**
 * Perform a Throttle Income API request
 *
 * @param  {Function} (period, dispatch, action, args, deceased, dependant)
 * @return {Promise}
 */
const throttledIncome = _.debounce((period, dispatch, action, args) => {
    return Api.calculators.monetary.income(period, state.theClaimant)
        .then((updated) => {
            Object.keys(updated)
                .forEach((key) => {
                    if (updated[key] !== null) {
                        return dispatch(action, {key, value: updated[key], ...args});
                    }
                });
        }, 500);
});

/**
 * Getters.
 *
 * @type {Object}
 */
const getters = {
    claimant: state => state.theClaimant,
    getClaimantValue: state => key => state.theClaimant[key],
    getClaimantDetail: state => key => state.claimantDetails[key],
    getClaimantInfoValue: state => key => state.claimInfo[key],
    getIncomeValue: state => key => state.theClaimant.income[key],
    getIncomePeriod: state => ({index, type}) => state.theClaimant.income[type + 'Periods'][index],
    getAllFutureLossOfIncomeTotal: state => state.theClaimant.income.futurePeriods
        .reduce((total, future) => total + numberVal(future.totalNetIncome), 0),
    getAllPastLossOfIncomeTotal: state => state.theClaimant.income.pastPeriods
        .reduce((total, past) => total + numberVal(past.totalNetIncome), 0),
    getIncomeStatus: state => state.updatingIncome,
};

// Actions
const actions = {
    /**
     * Save the step by submitting to the server.
     */
    save({getters, dispatch}, payload) {
        // We might change this to something which
        // is a bit cleaner...
        payload.data = state;

        return Api.claims.save(payload).then((response) => {
            return response;
        });
    },
    /**
     * Loads the step being received from the Server, to be placed in the state.
     */
    load({dispatch, action, commit}, step) {
        const data = _.deeply(_.mapKeys)(step, (value, key) => _.camelCase(key)).data;
        const theClaimant = data.theClaimant;
        const claimantDetails = data.claimantDetails;
        const claimInfo = data.claimInfo;

        commit(mutationTypes.SET, {key: 'theClaimant', value: theClaimant});
        commit(mutationTypes.SET, {key: 'claimantDetails', value: claimantDetails});
        commit(mutationTypes.SET, {key: 'claimInfo', value: claimInfo});

        dispatch('updateOnsetAndAssumedDod');
        dispatch('updateReductionInLife');
    },
    /**
     * Performs any processing once the data from the server has been stored.
     * This is a good place to do calculations or dispatch other actions which
     * are used to refresh/update calculated data on other steps/components.
     */
    process({commit, dispatch}) {
        // This is not currently used.
    },
    /**
     * Validates the step once any processing is complete.
     *
     * @param commit
     * @param dispatch
     * @param state
     */
    validate({commit, dispatch, state, rootState}) {
        let updateRules = _.clone(rules);

        if (rootState.user.user.hideNames) {
            delete updateRules['theClaimant.name'];
        }
        dispatch('lost/step/setStepNumberValid', {
            stepNo: 0,
            valid: (new Validator(state, updateRules)).validate(),
        }, {root: true});
    },
    /**
     * Resets the step back to defaults
     */
    reset({commit, dispatch}) {
        dispatch('lost/step/setStepNumberValid', {
            stepNo: 0,
            valid: false,
        }, {root: true});

        commit(mutationTypes.RESET);
    },
    /**
     * Reset Future Employment Income
     */
    resetFutureEmploymentIncome({commit, dispatch, state}) {
        state.theClaimant.income.futurePeriods.forEach((oldPeriod, index) => {
            let period = oldPeriod;
            period.employmentIncome = 0;
            dispatch('updateIncomePeriod', {index, period, type: 'future'});
        });
    },
    /**
     * Update a piece of Claimant data.
     */
    updateClaimantValue({commit, dispatch}, {key, value}) {
        commit(mutationTypes.UPDATE_CLAIMANT_VALUE, {key, value});

        if (key === 'dateOfBirth') {
            return Promise.all([
                dispatch('updateLifeExpectancy', {lifeExpectancy: state.theClaimant.lifeExpectancy}),
                dispatch('updateRetirementAgeAndDate'),
            ]);
        }

        if (key === 'employedButFor') {
            return value ? commit(mutationTypes.RESET_EMPLOYED_INCOME) : commit(mutationTypes.RESET_PENSION_INCOME);
        }
    },
    /**
     * Update a piece of Claimant's Details data.
     */
    updateClaimantDetail({commit}, {key, value}) {
        commit(mutationTypes.UPDATE_CLAIMANT_DETAIL, {key, value});
    },

    /**
     * Update the Claimant's Assumed Life Expectancy data.
     */
    updateClaimantAssumedLifeExpectancy({dispatch, state, rootGetters}, {assumedLifeExpectancy}) {
        let assumedLifeExpectancyResult = calcAssumedLifeExpectancy({state, assumedLifeExpectancy});

        return updateObject(dispatch, 'updateClaimantAssumedLifeExpectancyValue', assumedLifeExpectancyResult)
            .then(() => throttleUpdateLife(dispatch, rootGetters, 'updateClaimantAssumedLifeExpectancyValue', state.theClaimant, assumedLifeExpectancyResult));
    },
    /**
     * Update a piece of the Claimant's Assumed Life Expectancy data.
     */
    updateClaimantAssumedLifeExpectancyValue({commit, state, dispatch}, {key, value}) {
        if (state.theClaimant.assumedLifeExpectancy[key] !== value) {
            commit(mutationTypes.UPDATE_LIFE_ASSUMED_EXPECTANCY, {key, value});
            dispatch('updateOnsetAndAssumedDod');
            dispatch('updateReductionInLife');
        }
    },
    /**
     * Update the Period Between Onset of Symptoms and Assumed DOD
     */
    updateOnsetAndAssumedDod({state, dispatch}) {
        if (state.theClaimant.assumedLifeExpectancy.dateOfDeath && state.theClaimant.onsetOfSymptoms) {
            let onsetAndAssumedDod = moment(state.theClaimant.assumedLifeExpectancy.dateOfDeath, 'DD/MM/YYYY')
                .diff(moment(state.theClaimant.onsetOfSymptoms, 'DD/MM/YYYY'), 'days') / 365.25;
            dispatch('lost/claimInfo/updateClaimInfoValue',
                {key: 'periodBetweenOnsetAndAssumedDod', value: onsetAndAssumedDod}, {root: true});
        }
        dispatch('updateAgeAtOnset');
    },
    /**
     * Update the Claimant's age at onset of symptoms
     */
    updateAgeAtOnset({state, commit}) {
        let ageAtOnset = moment(state.theClaimant.onsetOfSymptoms, 'DD/MM/YYYY')
            .diff(moment(state.theClaimant.dateOfBirth, 'DD/MM/YYYY'), 'days') / 365.25;

        commit(mutationTypes.UPDATE_CLAIMANT_VALUE, {key: 'ageAtOnset', value: ageAtOnset});
    },
    /**
     * Update the Reduction in Claimant's Life Expectancy
     */
    updateReductionInLife({state, dispatch}) {
        if (state.theClaimant.assumedLifeExpectancy.ageAtDeath && state.theClaimant.lifeExpectancy.ageAtDeath) {
            let claimantReductionInLife = state.theClaimant.lifeExpectancy.ageAtDeath - state.theClaimant.assumedLifeExpectancy.ageAtDeath;
            dispatch('lost/claimInfo/updateClaimInfoValue',
                {key: 'claimantReductionInLife', value: claimantReductionInLife}, {root: true});
        }
    },
    /**
     * Update the Claimant's Life Expectancy data.
     */
    updateLifeExpectancy({dispatch, state, rootGetters}, {lifeExpectancy}) {
        return updateObject(dispatch, 'updateLifeExpectancyValue', lifeExpectancy)
            .then(() => throttleUpdateLife(dispatch, rootGetters, 'updateLifeExpectancyValue', state.theClaimant, lifeExpectancy));
    },
    /**
     * Update a piece of the Claimant's Life Expectancy data.
     */
    updateLifeExpectancyValue({commit, state}, {key, value}) {
        if (state.theClaimant.lifeExpectancy[key] !== value) {
            commit(mutationTypes.UPDATE_LIFE_EXPECTANCY, {key, value});
        }
    },

    /**
     * Update the Claimant's Income data.
     */
    updateClaimantIncome({dispatch}, {income}) {
        return updateObject(dispatch, 'updateIncomeValue', income)
    },
    /**
     * Update a piece of the Claimant's Income data.
     */
    updateIncomeValue({commit, state, dispatch}, {key, value}) {
        if (state.theClaimant.income[key] !== value) {
            commit(mutationTypes.UPDATE_INCOME, {key, value});
        }

        if (key === 'contingencyMultiplier' || key === 'employedAtDateOfDeath') {
            return dispatch('calculateLossOfEarnings');
        }
    },

    /**
     * Calculate loss of earnings
     *
     * @return promise
     */
    calculateLossOfEarnings: _.debounce(({state, dispatch}) => {
        let claimant = state.theClaimant;

        if (
            !claimant.income.contingencyMultiplier
            || claimant.employedAtAccident === null
            || claimant.levelOfEducation === null
            || claimant.disabledAtAccident === null
        ) {
            return;
        }

        return Api.calculators.monetary.employmentContingencyDiscount({
            age: moment(state.claimInfo.dateOfAssumedSettlement, 'DD/MM/YYYY')
                .diff(moment(claimant.dateOfBirth, 'DD/MM/YYYY'), 'years'),
            disabled: claimant.disabledAtAccident,
            employment: claimant.employedAtAccident ? 'employed' : 'notEmployed',
            education: claimant.levelOfEducation,
            gender: claimant.gender,
        })
            .then((data) => {
                dispatch('updateIncomeValue', {key: 'multiplier', value: data.multiplier})
            });
    }, 500),

    /**
     * Add an income period to the claimant
     */
    addIncomePeriod({commit, state}, type) {
        let periods = state.theClaimant.income[type + 'Periods'],
            start = findNextPeriodStart(periods, state.theClaimant.onsetOfSymptoms),
            end = findNextPeriodEnd(periods, state.theClaimant.assumedLifeExpectancy.dateOfDeath);

        if (moment(start, 'DD/MM/YYYY').isAfter(moment(end, 'DD/MM/YYYY'))) {
            start = end;
        }

        if (type === 'future') {
            start = findNextPeriodStart(periods, state.theClaimant.assumedLifeExpectancy.dateOfDeath);
            end = findNextPeriodEnd(periods, state.theClaimant.lifeExpectancy.dateOfDeath);
        }

        commit(mutationTypes.ADD_INCOME_PERIOD, {
            type, period: defaultIncomePeriodTemplate({
                start: start,
                end: end,
            })
        })
    },
    /**
     * Update an income period of the claimant
     */
    updateIncomePeriod({commit, dispatch, state}, {index, period, type}) {
        if (!state.updatingIncome) {
            commit(mutationTypes.UPDATING_INCOME_PERIOD);
        }

        let proms = Object.keys(period)
            .map(key => dispatch('updateIncomePeriodValue', {type, key, value: period[key], index}));

        if (period.endOfPeriod === ""
            || period.endOfPeriod === null
            || period.startOfPeriod === ""
            || period.startOfPeriod === null
            || (period.employmentIncome === "" || period.pensionIncome === "")
            || (period.employmentIncome === null || period.pensionIncome === null)) {
            return;
        }

        Promise.all(proms).then(() => {
            return Promise.all([throttledIncome(period, dispatch, 'updateIncomePeriodValue', {index, type})])
        }).then(() => {
            if (state.updatingIncome) {
                commit(mutationTypes.UPDATING_INCOME_PERIOD);
            }
        })
    },
    /**
     * Update an income period value of the claimant
     */
    updateIncomePeriodValue({commit, state}, {index, key, value, type}) {
        let currentPeriod = state.theClaimant.income[type + 'Periods'][index];
        if (currentPeriod[key] === value) {
            return;
        }
        commit(mutationTypes.UPDATE_INCOME_PERIOD, {index, key, value, type});
    },
    /**
     * delete an income period of the claimant
     */
    deleteIncomePeriod({commit, state, dispatch}, {index, type}) {
        commit(mutationTypes.DELETE_INCOME_PERIOD, {index, type});
        if (!state.theClaimant.income[type + 'Periods'].length) {
            return dispatch('addIncomePeriod', {type});
        }
    },
    /**
     * Update the state pension age
     */
    updateRetirementAgeAndDate: _.debounce(({state, commit, dispatch}) => {
        if (!(new Validator(state, {'theClaimant.dateOfBirth': {type: 'date', rules: 'required'}})).validate()) {
            return;
        }

        if (state.theClaimant.income.retirementAgeCalc === 'auto') {
            let previousRetirementDate = state.theClaimant.income.retirementDate;
            return Api.calculators.dates.statePensionAge(state.theClaimant)
                .then((data) => {
                    commit(mutationTypes.UPDATE_STATE_PENSION_AGE, {
                        key: 'retirementAge',
                        value: data.statePension.retirementAge
                    });
                    commit(mutationTypes.UPDATE_STATE_PENSION_AGE, {
                        key: 'retirementDate',
                        value: data.statePension.retirementDate
                    });
                    return dispatch('updateIncomePeriodForRetirementChange', previousRetirementDate);
                });
        }
    }, 500),
    /**
     * Update income period for retirement
     */
    updateIncomePeriodForRetirementChange({state, commit}, previousRetirementDate) {
        let retirementDate = state.theClaimant.income.retirementDate;
        let periods = state.theClaimant.income.futurePeriods;
        if (!periods.length) {
            return;
        }
        let end = periods[periods.length - 1].endOfPeriod;
        splitPeriodsForRetirement(
            commit,
            mutationTypes.UPDATE_INCOME_PERIOD,
            mutationTypes.ADD_INCOME_PERIOD,
            previousRetirementDate,
            retirementDate,
            periods,
            end,
            false,
            0,
            'future'
        );
    },
    /**
     * Update the claim info
     */
    updateClaimInfoValue({commit, dispatch, rootGetters}, {key, value}) {
        commit(mutationTypes.UPDATE_CLAIM_INFO_VALUE, {key, value});
        if (key === 'proceduralStatus') {
            return dispatch('lost/headsOfLoss/proceduralStatusChanges', value, {root: true});
        }
        if (key === "discountRate" && rootGetters['lost/step/valid'](3)) {
            dispatch('lost/lossOfIncome/refresh', value, {root: true});
        }
    },
    /**
     * Check if there are income dates that need updating
     */
    checkIncomePeriods({dispatch, getters}) {
        if (getters['getIncomePeriod']({type: 'future', index: 0})) {
            dispatch('updateIncomePeriodValue', {
                index: 0,
                key: 'startOfPeriod',
                value: state.theClaimant.assumedLifeExpectancy.dateOfDeath,
                type: 'future'
            });
        }

        if (getters['getClaimantValue']('income').pastPeriods.length) {
            dispatch('updateIncomePeriodValue', {
                index: (getters['getClaimantValue']('income').pastPeriods.length - 1),
                key: 'endOfPeriod',
                value: state.theClaimant.assumedLifeExpectancy.dateOfDeath,
                type: 'past'
            });
        }
    }
};

// Mutations
const mutations = {
    /**
     * Set an object on the state.
     */
    [mutationTypes.SET](state, payload) {
        // Might be able to do this nicer with Object.assign
        Vue.set(state, payload.key, payload.value);
    },
    /**
     * Update claimant data in the state.
     */
    [mutationTypes.UPDATE_CLAIMANT_VALUE](state, payload) {
        state.theClaimant[payload.key] = payload.value;
    },
    /**
     * Update claimant detail in the state.
     */
    [mutationTypes.UPDATE_CLAIMANT_DETAIL](state, payload) {
        state.claimantDetails[payload.key] = payload.value;
    },
    /**
     * Update claim info in the state.
     */
    [mutationTypes.UPDATE_CLAIM_INFO_VALUE](state, payload) {
        state.claimInfo[payload.key] = payload.value;
    },
    /**
     * Update claimant life expectancy in the state.
     */
    [mutationTypes.UPDATE_INCOME](state, {key, value}) {
        state.theClaimant.income[key] = value;
    },
    /**
     * Update claimant income in the state.
     */
    [mutationTypes.UPDATE_LIFE_EXPECTANCY](state, {key, value}) {
        state.theClaimant.lifeExpectancy[key] = value;
    },
    /**
     * Update claimant assumed life expectancy in the state.
     */
    [mutationTypes.UPDATE_LIFE_ASSUMED_EXPECTANCY](state, {key, value}) {
        state.theClaimant.assumedLifeExpectancy[key] = value;
    },
    /**
     * add claimant income period in the state.
     */
    [mutationTypes.ADD_INCOME_PERIOD](state, payload) {
        state.theClaimant.income[payload.type + 'Periods'].push(payload.period);
        state.theClaimant.income.netPensionIncome += payload.period.netPensionIncome;
        state.theClaimant.income.grossPensionIncome += payload.period.grossPensionIncome;
        state.theClaimant.income.netIncome += payload.period.netIncome;
        state.theClaimant.income.grossIncome += payload.period.grossIncome;
    },
    /**
     * update claimant income period in the state.
     */
    [mutationTypes.UPDATE_INCOME_PERIOD](state, {key, value, index, type}) {
        state.theClaimant.income[type + 'Periods'][index][key] = value;
    },
    /**
     * delete claimant income period in the state.
     */
    [mutationTypes.DELETE_INCOME_PERIOD](state, payload) {
        state.theClaimant.income[payload.type + 'Periods'].splice(payload.index, 1);
    },
    /**
     * Reset state.
     */
    [mutationTypes.RESET](state) {
        state.theClaimant = personTemplate();
        state.claimantDetails.diseaseOrAccident = null;
        state.claimantDetails.accidentType = null;
        state.claimantDetails.accidentTypeOther = '';
        state.claimantDetails.diseaseType = null;
        state.claimantDetails.diseaseTypeOther = '';
        state.claimantDetails.claimantReference = null;
        state.claimantDetails.yourReference = null;
        state.claimantDetails.claimNarrative = null;
        state.claimantDetails.defendantName.id = null;
        state.claimantDetails.defendantName.name = null;
        state.claimantDetails.defendantName.type = null;
        state.claimantDetails.claimantSolicitor.id = null;
        state.claimantDetails.claimantSolicitor.name = null;
        state.claimantDetails.claimantSolicitor.type = null;
        state.claimInfo.discountRate = -0.25;
        state.claimInfo.periodBetweenOnsetAndAssumedDod = 0;
        state.claimInfo.claimantReductionInLife = 0;
        state.claimInfo.proceduralStatus = 'pre-lit';
        state.claimInfo.dateOfIssueProceedings = null;
        state.claimInfo.dateOfServiceProceedings = null;
        state.claimInfo.dateOfAssumedSettlement = null;
    },
    /**
     * Update state pension age/date.
     */
    [mutationTypes.UPDATE_STATE_PENSION_AGE](state, {key, value}) {
        state.theClaimant.income[key] = value;
    },
    [mutationTypes.RESET_EMPLOYED_INCOME](state) {
        state.theClaimant.income.pastPeriods.map(period => {
            period.employmentIncome = 0;
            if (period.pensionNetGross==='net') {
                period.totalNetIncome = period.pensionIncome;
            } else {
                period.totalGrossIncome = period.pensionIncome;
            }
            return period;
        });
        state.theClaimant.income.futurePeriods.map(period => {
            period.employmentIncome = 0;
            if (period.pensionNetGross === 'net') {
                period.totalNetIncome = period.pensionIncome;
            } else {
                period.totalGrossIncome = period.pensionIncome;
            }
            return period;
        });
    },
    [mutationTypes.RESET_PENSION_INCOME](state) {
        state.theClaimant.income.pastPeriods.map(period => {
            period.pensionIncome = 0;
            if (period.employmentNetGross === 'net') {
                period.totalNetIncome = period.employmentIncome;
            } else {
                period.totalGrossIncome = period.employmentIncome;
            }
            return period;
        });
        state.theClaimant.income.futurePeriods.map(period => {
            period.pensionIncome = 0;
            if (period.employmentNetGross === 'net') {
                period.totalNetIncome = period.employmentIncome;
            } else {
                period.totalGrossIncome = period.employmentIncome;
            }
            return period;
        });
    },
    [mutationTypes.UPDATING_INCOME_PERIOD](state) {
        state.updatingIncome = !state.updatingIncome;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/lost/claimant.js